import React, { createContext, useState, useEffect } from "react";
import AccountsService from "../utils/accountsService";

export const AppContext = createContext();

export const AppProvider = ({ children }) => {
  // Function to update menus dynamically

  const [company, setCompany] = useState(() => {
    const storedCompany = localStorage.getItem("company");
    return storedCompany ? JSON.parse(storedCompany) : { balance: 0 };
  });

  const [accounts, setAccounts] = useState(() => {
    const storedAccounts = localStorage.getItem("accounts");
    return storedAccounts ? JSON.parse(storedAccounts) : [];
  });

  const [theme, setTheme] = useState(() => {
    const storedTheme = localStorage.getItem("theme");
    return storedTheme || "light";
  });

  const [skin, setSkin] = useState(() => {
    const storedSkin = localStorage.getItem("skin");
    return storedSkin || "skin-black";
  });

  // Navigation layout: "sidebar" (default left sidebar) or "top" (horizontal bar).
  const [navLayout, setNavLayout] = useState(() => {
    const storedNavLayout = localStorage.getItem("navLayout");
    return storedNavLayout || "sidebar";
  });

  const toggleTheme = () => {
    setTheme((prevTheme) => {
      const newTheme = prevTheme === "light" ? "dark" : "light";
      return newTheme;
    });
  };

  const changeSkin = (newSkin) => {
    setSkin(newSkin);
  };

  const toggleNavLayout = () => {
    setNavLayout((prev) => (prev === "sidebar" ? "top" : "sidebar"));
  };

  // Legacy balance update function for backward compatibility
  const updateBalance = (newBalance) => {
    setCompany((prevCompany) => ({
      ...prevCompany,
      balance: newBalance,
    }));
  };

  // New account management functions
  const updateAccounts = (newAccounts) => {
    setAccounts(newAccounts);
  };

  const updateAccountBalance = (accountId, newBalance) => {
    setAccounts(prevAccounts =>
      prevAccounts.map(account =>
        account.id === accountId
          ? { ...account, balance: newBalance }
          : account
      )
    );
  };

  // Fetch accounts whenever company changes
  useEffect(() => {
    const fetchAccounts = async () => {
      if (company?.id) {
        try {
          const token = localStorage.getItem("token");
          const accountData = await AccountsService.fetchActiveAccounts(company.id, token);
          setAccounts(accountData);
        } catch (error) {
          console.error("Error fetching accounts:", error);
        }
      }
    };

    fetchAccounts();
  }, [company?.id]);

  const [menus, setMenus] = useState(() => {
    const storedMenus = localStorage.getItem("menus");
    return storedMenus ? JSON.parse(storedMenus) : [];
  });

  const updateMenus = (newMenus) => {
    setMenus(newMenus);
  };

  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [cards, setCards] = useState(() => {
    const storedCards = localStorage.getItem("cards");
    return storedCards ? JSON.parse(storedCards) : [];
  });

  // Persist data to localStorage whenever it changes
  useEffect(() => {
    if (user) localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("menus", JSON.stringify(menus));
    if (company) localStorage.setItem("company", JSON.stringify(company));
    if (accounts) localStorage.setItem("accounts", JSON.stringify(accounts));
    if (cards) localStorage.setItem("cards", JSON.stringify(cards));
    localStorage.setItem("theme", theme);
    localStorage.setItem("skin", skin);
    localStorage.setItem("navLayout", navLayout);
  }, [user, menus, company, accounts, cards, theme, skin, navLayout]);

  useEffect(() => {
    // Check token expiration on app load
    const checkAuth = () => {
      const token = localStorage.getItem("token");
      const expires = localStorage.getItem("expires");

      if (token && expires && Date.now() > parseInt(expires)) {
        logout();
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const expires = localStorage.getItem("expires");
      if (expires && Date.now() > parseInt(expires)) {
        logout();
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  const logout = () => {
    // Clear all state
    setUser(null);
    setCompany(null);
    setAccounts([]);
    setCards([]);
    setMenus([]);
    
    // Clear localStorage completely
    localStorage.clear();
    
    // Ensure isAuth is explicitly set to false
    localStorage.setItem("isAuth", "false");
    
    // Use a direct href with the full path to avoid any routing issues
    const baseUrl = window.location.origin;
    window.location.href = `${baseUrl}/login`;
  };

  return (
    <AppContext.Provider
      value={{
        user,
        setUser,
        company,
        updateBalance,
        setCompany,
        accounts,
        updateAccounts,
        updateAccountBalance,
        cards,
        setCards,
        menus,
        setMenus,
        logout,
        updateMenus,
        theme,
        toggleTheme,
        skin,
        changeSkin,
        navLayout,
        toggleNavLayout,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};